"use client";

import { useEffect, useState } from "react";

/**
 * An on-screen readout of the numbers that decide whether the ladder clears the header.
 *
 * WHY THIS EXISTS. The heading and the top of the member count kept ending up underneath
 * the site header on Tom's iPhone, and no viewport size, text size or engine tested on
 * this machine reproduced it: the current production build passes the same stress run that
 * the fix passes. Safari Web Inspector would answer it, but the phone shows as unavailable
 * to this Mac and is not on USB, so there is nothing to attach to.
 *
 * So the device reports its own measurements instead. Open the landing page with
 * ?debug=layout on the handset that shows the fault, screenshot the panel, and the numbers
 * say which assumption is wrong rather than which one seems likely.
 *
 * It renders for nobody unless that query parameter is present.
 */
export function LayoutProbe() {
  const [readout, setReadout] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has("debug")) return;

    const read = () => {
      const header = document.querySelector("header");
      const stage = document.querySelector(".gs-ladder-stage");
      const heading = document.getElementById("matching-heading");
      const count = document.querySelector(".gs-ladder-count");
      const top = (el: Element | null) => (el ? Math.round(el.getBoundingClientRect().top) : NaN);

      const probe = document.createElement("div");
      probe.style.cssText = "position:fixed;top:env(safe-area-inset-top);left:0;height:0;width:0;";
      document.body.appendChild(probe);
      const inset = Math.round(probe.getBoundingClientRect().top);
      probe.remove();

      const headerBottom = header ? Math.round(header.getBoundingClientRect().bottom) : NaN;
      const headingTop = top(heading);
      const countTop = top(count);

      setReadout({
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        visual: `${Math.round(window.visualViewport?.width ?? 0)}x${Math.round(window.visualViewport?.height ?? 0)}`,
        dpr: String(window.devicePixelRatio),
        safeTop: `${inset}`,
        headerH: header ? `${Math.round(header.getBoundingClientRect().height)}` : "none",
        headerBottom: `${headerBottom}`,
        varHeaderH: getComputedStyle(document.documentElement).getPropertyValue("--gs-header-h").trim() || "unset",
        stageTop: `${top(stage)}`,
        headingTop: `${headingTop}`,
        countTop: `${countTop}`,
        headingClear: `${headingTop - headerBottom}`,
        countClear: `${countTop - headerBottom}`,
      });
    };

    read();
    const onScroll = () => requestAnimationFrame(read);
    document.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onScroll, { passive: true });
    window.visualViewport?.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onScroll);
      window.visualViewport?.removeEventListener("resize", onScroll);
    };
  }, []);

  if (!readout) return null;

  /* Negative "clear" values are the fault: they mean that element is behind the header. */
  const bad = (k: string) => k.endsWith("Clear") && Number(readout[k]) < 0;

  return (
    <div
      style={{
        position: "fixed",
        right: 8,
        bottom: 8,
        zIndex: 2147483647,
        background: "rgba(9,15,57,0.94)",
        color: "#fff",
        font: "600 11px/1.45 ui-monospace, Menlo, monospace",
        padding: "10px 12px",
        borderRadius: 10,
        maxWidth: "62vw",
        pointerEvents: "none",
      }}
    >
      {Object.entries(readout).map(([k, v]) => (
        <div key={k} style={{ color: bad(k) ? "#ff8a8a" : undefined }}>
          {k}={v}
        </div>
      ))}
    </div>
  );
}
